import { Block, Workflow, Agent, AgentMonthlyMetrics, AgentMetricsSummary, BillingMetrics, MetricsTimeRange } from './types';

// Use different API URLs for development and production
const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? '/api'  // Use relative URL in production (served by same server)
  : (process.env.REACT_APP_API_URL || 'http://localhost:8080/api'); // Use localhost in development

// Helper function to get authorization headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  console.log('getAuthHeaders - token exists:', !!token);
  if (token) {
    console.log('getAuthHeaders - token prefix:', token.substring(0, 10) + '...');
  }
  
  return {
    'Content-Type': 'application/json',
    'Authorization': token ? `Bearer ${token}` : '',
  };
};

export const api = {
  // Authentication methods
  auth: {
    // Check if registration is enabled
    async getRegistrationStatus() {
      const response = await fetch(`${API_BASE_URL}/auth/registration-status`);
      if (!response.ok) {
        throw new Error('Failed to check registration status');
      }
      return await response.json();
    },

    // Login a user
    async login(email: string, password: string) {
      console.log('API: Login attempt for', email);
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Invalid credentials' }));
        throw new Error(errorData.error || 'Login failed');
      }
      
      return await response.json();
    },
    
    // Register a new user
    async register(name: string, email: string, password: string) {
      const response = await fetch(`${API_BASE_URL}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name, email, password }),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Registration failed' }));
        throw new Error(errorData.error || 'Registration failed');
      }
      
      return await response.json();
    },
    
    // Get current user profile
    async getProfile() {
      const response = await fetch(`${API_BASE_URL}/users/me`, {
        headers: getAuthHeaders(),
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch user profile');
      }
      
      return await response.json();
    },
    
    // Update user profile
    async updateProfile(userData: { name?: string; email?: string }) {
      const response = await fetch(`${API_BASE_URL}/users/me`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(userData),
      });
      
      if (!response.ok) {
        throw new Error('Failed to update user profile');
      }
      
      return await response.json();
    },
    
    // Change password
    async changePassword(currentPassword: string, newPassword: string) {
      const response = await fetch(`${API_BASE_URL}/users/me/password`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to change password' }));
        throw new Error(errorData.error || 'Failed to change password');
      }
      
      return await response.json();
    },
    
    // Regenerate API key
    async regenerateApiKey() {
      const response = await fetch(`${API_BASE_URL}/users/me/api-key`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to regenerate API key' }));
        throw new Error(errorData.error || 'Failed to regenerate API key');
      }
      
      return await response.json();
    },
    
    // Get current agent API key
    async getAgentApiKey(): Promise<{ agentApiKey: string }> {
      const response = await fetch(`${API_BASE_URL}/users/me/agent-api-key`, {
        headers: getAuthHeaders(),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to get agent API key' }));
        throw new Error(errorData.error || 'Failed to get agent API key');
      }
      
      return await response.json();
    },
    
    // Generate or regenerate agent API key
    async regenerateAgentApiKey(): Promise<{ agentApiKey: string }> {
      const response = await fetch(`${API_BASE_URL}/users/me/agent-api-key`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to regenerate agent API key' }));
        throw new Error(errorData.error || 'Failed to regenerate agent API key');
      }
      
      return await response.json();
    },
    
    // Delete agent API key
    async deleteAgentApiKey() {
      const response = await fetch(`${API_BASE_URL}/users/me/agent-api-key`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to delete agent API key' }));
        throw new Error(errorData.error || 'Failed to delete agent API key');
      }
      
      return await response.json();
    },
  },

  // Admin methods
  admin: {
    // Get all users
    async getUsers() {
      const response = await fetch(`${API_BASE_URL}/admin/users`, {
        headers: getAuthHeaders(),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.error || `Failed to fetch users (${response.status})`);
      }
      
      return await response.json();
    },
    
    // Get user by ID
    async getUser(id: string) {
      const response = await fetch(`${API_BASE_URL}/admin/users/${id}`, {
        headers: getAuthHeaders(),
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch user');
      }
      
      return await response.json();
    },
    
    // Create new user
    async createUser(userData: { name: string, email: string, password: string, role: string }) {
      const response = await fetch(`${API_BASE_URL}/admin/users`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(userData),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.error || 'Failed to create user');
      }
      
      return await response.json();
    },
    
    // Update user
    async updateUser(id: string, userData: { name?: string, email?: string, role?: string }) {
      const response = await fetch(`${API_BASE_URL}/admin/users/${id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(userData),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.error || 'Failed to update user');
      }
      
      return await response.json();
    },
    
    // Delete user
    async deleteUser(id: string) {
      const response = await fetch(`${API_BASE_URL}/admin/users/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.error || 'Failed to delete user');
      }
      
      return true;
    },

    // Get registration settings
    async getRegistrationSettings() {
      const response = await fetch(`${API_BASE_URL}/admin/settings/registration`, {
        headers: getAuthHeaders(),
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch registration settings');
      }
      
      return await response.json();
    },

    // Update registration settings
    async updateRegistrationSettings(enabled: boolean) {
      const response = await fetch(`${API_BASE_URL}/admin/settings/registration`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ enabled }),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.error || 'Failed to update registration settings');
      }
      
      return await response.json();
    },

    // Export system backup
    async exportBackup() {
      const response = await fetch(`${API_BASE_URL}/admin/backup/export`, {
        headers: getAuthHeaders(),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.error || 'Failed to export backup');
      }
      
      return await response.json();
    },

    // Import system backup
    async importBackup(backupData: any) {
      const response = await fetch(`${API_BASE_URL}/admin/backup/import`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(backupData),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.error || 'Failed to import backup');
      }
      
      return await response.json();
    },

    // Model management
    async getModels(showAll: boolean = false) {
      const params = showAll ? '?all=true' : '';
      const response = await fetch(`${API_BASE_URL}/admin/models${params}`, {
        headers: getAuthHeaders(),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.error || 'Failed to fetch models');
      }
      
      return await response.json();
    },

    async updateModelStatus(modelId: string, enabled: boolean) {
      const response = await fetch(`${API_BASE_URL}/admin/models/status`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ modelId, enabled }),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.error || 'Failed to update model status');
      }
      
      return await response.json();
    },

    async updateModelTimeout(modelId: string, timeoutSeconds: number) {
      const response = await fetch(`${API_BASE_URL}/admin/models/timeout`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ modelId, timeoutSeconds }),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.error || 'Failed to update model timeout');
      }
      
      return await response.json();
    },

    async updateModelMaxOutputTokens(modelId: string, maxOutputTokens: number) {
      const response = await fetch(`${API_BASE_URL}/admin/models/max-output-tokens`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ modelId, maxOutputTokens }),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.error || 'Failed to update model max output tokens');
      }
      
      return await response.json();
    },

    async rescanModels() {
      const response = await fetch(`${API_BASE_URL}/admin/models/rescan`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.error || 'Failed to rescan models');
      }
      
      return await response.json();
    },

    async setDefaultModel(modelId: string) {
      const response = await fetch(`${API_BASE_URL}/admin/models/default`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ modelId }),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.error || 'Failed to set default model');
      }
      
      return await response.json();
    },

    async updateModelCacheable(modelId: string, cacheable: boolean) {
      const response = await fetch(`${API_BASE_URL}/admin/models/cacheable`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ modelId, cacheable }),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.error || 'Failed to update model cacheable status');
      }
      
      return await response.json();
    },

    async getProviders() {
      const response = await fetch(`${API_BASE_URL}/admin/models/providers`, {
        headers: getAuthHeaders(),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.error || 'Failed to fetch providers');
      }
      
      return await response.json();
    },

    // API Key Management
    async getAPIKeys() {
      const response = await fetch(`${API_BASE_URL}/admin/api-keys`, {
        headers: getAuthHeaders(),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.error || 'Failed to fetch API keys');
      }
      
      return await response.json();
    },

    async createAPIKey(provider: string, name: string, keyValue: string) {
      const response = await fetch(`${API_BASE_URL}/admin/api-keys`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ provider, name, keyValue }),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.error || 'Failed to create API key');
      }
      
      return await response.json();
    },

    async updateAPIKey(id: string, data: { name?: string; keyValue?: string; isActive?: boolean }) {
      const response = await fetch(`${API_BASE_URL}/admin/api-keys/${id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(data),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.error || 'Failed to update API key');
      }
      
      return await response.json();
    },

    async deleteAPIKey(id: string) {
      const response = await fetch(`${API_BASE_URL}/admin/api-keys/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.error || 'Failed to delete API key');
      }
      
      return await response.json();
    },

    async toggleAPIKey(id: string, isActive: boolean) {
      const response = await fetch(`${API_BASE_URL}/admin/api-keys/${id}/toggle`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ isActive }),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.error || 'Failed to toggle API key');
      }
      
      return await response.json();
    },

    // Cache Management
    async wipeCache(confirmation: string) {
      const response = await fetch(`${API_BASE_URL}/cache/wipe`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
        body: JSON.stringify({ confirmation }),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.error || 'Failed to wipe cache');
      }
      
      return await response.json();
    },
  },

  // Get enabled models for block selection
  async getEnabledModels() {
    const response = await fetch(`${API_BASE_URL}/models/enabled`, {
      // No auth required - this is a public endpoint
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(errorData?.error || 'Failed to fetch enabled models');
    }
    
    return await response.json();
  },

  // Get all models (including deprecated ones) for block editors
  async getAllModels() {
    const response = await fetch(`${API_BASE_URL}/models/all`, {
      // No auth required - this is a public endpoint
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(errorData?.error || 'Failed to fetch all models');
    }
    
    return await response.json();
  },

  async getBlocks(): Promise<Block[]> {
    console.log('Fetching blocks from API...');
    const response = await fetch(`${API_BASE_URL}/blocks`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      console.error('Failed to fetch blocks:', response.status, response.statusText);
      throw new Error('Failed to fetch blocks');
    }
    const data = await response.json();
    console.log('Blocks API response:', data);
    
    // Check for categories in the response
    const categories = new Set(Array.isArray(data) 
      ? data.map((block: any) => block.category) 
      : data.blocks?.map((block: any) => block.category) || []);
    console.log('Block categories found:', Array.from(categories));
    
    // Count blocks by category
    const blocksByCategory = Array.isArray(data)
      ? data.reduce((acc: any, block: any) => {
          acc[block.category] = (acc[block.category] || 0) + 1;
          return acc;
        }, {})
      : (data.blocks || []).reduce((acc: any, block: any) => {
          acc[block.category] = (acc[block.category] || 0) + 1;
          return acc;
        }, {});
    console.log('Blocks by category:', blocksByCategory);
    
    // Log total of all blocks per type to diagnose any missing blocks
    const totalBlocks = Object.values(blocksByCategory).reduce((sum: number, count: any) => sum + count, 0);
    console.log(`Total blocks found: ${totalBlocks}`);
    if (blocksByCategory['Software Engineering']) {
      console.log(`Software Engineering blocks: ${blocksByCategory['Software Engineering']}`);
    } else {
      console.log('No Software Engineering blocks found!');
    }
    
    return Array.isArray(data) ? data : data.blocks || [];
  },

  async getBlock(id: string): Promise<Block> {
    const response = await fetch(`${API_BASE_URL}/blocks/${id}`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error('Failed to fetch block');
    }
    return response.json();
  },

  async createBlock(block: Omit<Block, 'id'>): Promise<Block> {
    console.log('Creating block:', block.name, 'Category:', block.category);
    try {
      const response = await fetch(`${API_BASE_URL}/blocks`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(block),
      });
      
      if (!response.ok) {
        console.error(`Failed to create block: ${block.name}. Status: ${response.status}`);
        const errorText = await response.text();
        console.error('Error details:', errorText);
        throw new Error(`Failed to create block: ${errorText}`);
      }
      
      const createdBlock = await response.json();
      console.log('Block created successfully:', createdBlock);
      return createdBlock;
    } catch (error) {
      console.error('Error creating block:', error);
      throw error;
    }
  },

  async updateBlock(id: string, block: Block): Promise<Block> {
    const response = await fetch(`${API_BASE_URL}/blocks/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(block),
    });
    if (!response.ok) {
      throw new Error('Failed to update block');
    }
    return response.json();
  },

  async deleteBlock(id: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/blocks/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error('Failed to delete block');
    }
  },

  async getWorkflows(): Promise<Workflow[]> {
    const response = await fetch(`${API_BASE_URL}/workflows`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error('Failed to fetch workflows');
    }
    const data = await response.json();
    console.log('Workflows API response:', data);
    
    // Handle both array responses and object responses with a workflows property
    if (Array.isArray(data)) {
      return data;
    } else if (data && typeof data === 'object') {
      return data.workflows || [];
    }
    
    return [];
  },

  async getWorkflow(id: string): Promise<Workflow> {
    const response = await fetch(`${API_BASE_URL}/workflows/${id}`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error('Failed to fetch workflow');
    }
    return response.json();
  },

  async createWorkflow(workflow: Omit<Workflow, 'id'>): Promise<Workflow> {
    // Get the current user from localStorage to add ownership info
    try {
      const userString = localStorage.getItem('user');
      if (userString) {
        const user = JSON.parse(userString);
        if (user?.id) {
          // Add the userId to the workflow before sending to server
          // Use the 'as any' cast because the Workflow type might not include userId
          (workflow as any).userId = user.id;
          console.log(`Adding current user ID (${user.id}) to workflow`);
        } else {
          console.warn('User found in localStorage but missing ID');
        }
      } else {
        console.warn('No user found in localStorage, workflow will have no owner');
      }
    } catch (err) {
      console.error('Error accessing user data from localStorage:', err);
    }
    
    console.log('Creating workflow with data:', workflow);
    
    const response = await fetch(`${API_BASE_URL}/workflows`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(workflow),
    });
    
    if (!response.ok) {
      throw new Error('Failed to create workflow');
    }
    
    const createdWorkflow = await response.json();
    
    // If the server didn't set a userId (which it doesn't in current implementation)
    // We manually ensure it has the right userId for client-side operations
    if (!createdWorkflow.userId && (workflow as any).userId) {
      createdWorkflow.userId = (workflow as any).userId;
      
      // Also update the workflow on the server to ensure persistence
      this.updateWorkflow(createdWorkflow.id, createdWorkflow)
        .then(() => console.log('Workflow ownership updated on server'))
        .catch(err => console.error('Failed to update workflow ownership on server:', err));
    }
    
    return createdWorkflow;
  },

  async updateWorkflow(id: string, workflow: Workflow): Promise<Workflow> {
    const response = await fetch(`${API_BASE_URL}/workflows/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(workflow),
    });
    if (!response.ok) {
      throw new Error('Failed to update workflow');
    }
    return response.json();
  },

  async deleteWorkflow(id: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/workflows/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error('Failed to delete workflow');
    }
  },

  async executeWorkflow(id: string, inputs: Record<string, any> = {}): Promise<{ executionId: string }> {
    const response = await fetch(`${API_BASE_URL}/workflows/${id}/execute`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(inputs),
    });
    if (!response.ok) {
      throw new Error('Failed to execute workflow');
    }
    return response.json();
  },

  async getWorkflowExecution(executionId: string): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/executions/${executionId}`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      // If we get a 401/403, try using the agent API key approach via the public endpoint
      if (response.status === 401 || response.status === 403) {
        try {
          return await this.getPublicWorkflowExecution(executionId);
        } catch (err) {
          console.error('Failed to get execution via public API:', err);
          throw new Error('Failed to fetch workflow execution: ' + (err instanceof Error ? err.message : String(err)));
        }
      }
      throw new Error('Failed to fetch workflow execution');
    }
    return response.json();
  },

  // New method using the public API with agent API key
  async getPublicWorkflowExecution(executionId: string): Promise<any> {
    // Get agent API key from user or fallback to server
    const apiKey = await this.getAgentApiKey();
    
    const response = await fetch(`${API_BASE_URL}/public/executions/${executionId}`, {
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': apiKey
      }
    });
    
    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Invalid API key. Please check your agent API key in your profile.');
      }
      throw new Error(`Failed to fetch execution (Status: ${response.status})`);
    }
    
    return response.json();
  },

  // Helper to get the agent API key from user or fallback to server
  async getAgentApiKey(): Promise<string> {
    // Check if the user is logged in and has an agent API key
    try {
      const userString = localStorage.getItem('user');
      if (userString) {
        const user = JSON.parse(userString);
        if (user?.agentApiKey) {
          console.log('Using user agent API key');
          return user.agentApiKey;
        }
      }
      
      // If no user agent API key, try to get via API if the user is logged in
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const result = await this.auth.getAgentApiKey();
          if (result && result.agentApiKey) {
            // Store in user object for future use
            if (userString) {
              const user = JSON.parse(userString);
              user.agentApiKey = result.agentApiKey;
              localStorage.setItem('user', JSON.stringify(user));
            }
            console.log('Retrieved agent API key from server');
            return result.agentApiKey;
          }
        } catch (error) {
          console.warn('Could not get agent API key from server:', error);
        }
      }
      
      // Fall back to server API key
      const serverKey = await this.getServerApiKey();
      console.log('Using server API key');
      return serverKey;
    } catch (error) {
      console.error('Error getting API key:', error);
      return this.getServerApiKey();
    }
  },

  // Save OpenAI API key to backend
  saveOpenAIKey: async (key: string): Promise<boolean> => {
    console.log('Saving OpenAI API key to backend...');
    try {
      const response = await fetch(`${API_BASE_URL}/settings/openai-key`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ key }),
      });
      
      if (!response.ok) {
        console.error('Failed to save OpenAI API key to backend:', response.status);
        return false;
      }
      
      const result = await response.json();
      const success = result.status === 'success';
      console.log('OpenAI API key saved to backend:', success ? 'success' : 'failed');
      
      return success;
    } catch (error) {
      console.error('Error saving OpenAI API key to backend:', error);
      return false;
    }
  },

  // Get OpenAI API key status from backend
  getOpenAIKeyStatus: async (): Promise<{ keyStatus: 'missing' | 'configured', keyPreview?: string }> => {
    try {
      const response = await fetch(`${API_BASE_URL}/settings/openai-key`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        console.error('Error fetching OpenAI API key status:', response.status);
        return { keyStatus: 'missing' };
      }
      
      const data = await response.json();
      return {
        keyStatus: data.key_status || 'missing',
        keyPreview: data.key_preview
      };
    } catch (error) {
      console.error('Error fetching OpenAI API key status:', error);
      return { keyStatus: 'missing' };
    }
  },
  
  // Validate OpenAI API key
  validateOpenAIKey: async (key: string): Promise<{ valid: boolean, error?: string }> => {
    console.log('Validating OpenAI API key...');
    try {
      const response = await fetch(`${API_BASE_URL}/settings/validate-openai-key`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ key }),
      });
      
      if (!response.ok) {
        console.error('Error validating OpenAI API key:', response.status);
        return { valid: false, error: 'Server error during validation' };
      }
      
      const result = await response.json();
      console.log('OpenAI API key validation result:', result.valid ? 'valid' : 'invalid');
      
      return {
        valid: result.valid,
        error: result.error
      };
    } catch (error) {
      console.error('Error validating OpenAI API key:', error);
      return { valid: false, error: 'Network error during validation' };
    }
  },

  // Test a prompt with OpenAI
  testPrompt: async (prompt: string, input: string, model?: string): Promise<{response: string, actualModel?: string, provider?: string, model?: string}> => {
    console.log('Testing prompt using backend API key');
    
    try {
      const response = await fetch(`${API_BASE_URL}/test-prompt`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          prompt,
          input,
          model,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error testing prompt:', errorText);
        throw new Error(`Error testing prompt: ${errorText}`);
      }

      const data = await response.json();
      return {
        response: data.response,
        actualModel: data.actualModel,
        provider: data.provider,
        model: data.model
      };
    } catch (error) {
      console.error('Error testing prompt:', error);
      throw error;
    }
  },

  // Test a prompt with OpenAI with streaming
  testPromptStream: async (
    prompt: string,
    input: string,
    onData: (data: string) => void,
    model?: string
  ): Promise<void> => {
    console.log('Testing prompt stream using backend API key');
    
    try {
      const response = await fetch(`${API_BASE_URL}/test-prompt-stream`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          prompt,
          input,
          model,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error testing prompt stream:', errorText);
        throw new Error(`Error testing prompt stream: ${errorText}`);
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('Failed to get response reader');
      }

      const decoder = new TextDecoder();
      let buffer = '';

      // Process the streaming response
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        
        // Process lines in the buffer
        let lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep the last (potentially incomplete) line in the buffer
        
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const content = line.substring(6).trim();
            if (content !== '[DONE]') {
              // Pass the content directly without further processing
              // This ensures spaces and formatting are preserved
              onData(content);
            }
          }
        }
      }
      
      // Process any remaining data in the buffer
      if (buffer && buffer.startsWith('data: ')) {
        const content = buffer.substring(6).trim();
        if (content !== '[DONE]') {
          onData(content);
        }
      }
      
    } catch (error) {
      console.error('Error testing prompt stream:', error);
      throw error;
    }
  },

  async getAgents(): Promise<Agent[]> {
    const response = await fetch(`${API_BASE_URL}/agents`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error('Failed to get agents');
    }
    return response.json();
  },

  async getAgent(id: string): Promise<Agent> {
    const response = await fetch(`${API_BASE_URL}/agents/${id}`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error('Failed to get agent');
    }
    return response.json();
  },

  async createAgent(agent: Omit<Agent, 'id' | 'createdAt' | 'updatedAt'>): Promise<Agent> {
    console.log('Creating agent with data:', agent);
    
    if (!agent.workflowId) {
      throw new Error('A valid workflow ID is required to create an agent');
    }
    
    // First, verify workflow exists and belongs to the user
    try {
      const workflow = await this.getWorkflow(agent.workflowId);
      console.log('Workflow validated:', workflow.id);
    } catch (err) {
      console.error('Workflow validation error:', err);
      throw new Error('The selected workflow does not exist or you do not have access to it');
    }
    
    const token = localStorage.getItem('token');
    if (!token) {
      console.error('Authentication token missing');
      throw new Error('You need to be logged in to create an agent');
    }
    
    console.log('Using authentication token:', token.substring(0, 10) + '...');
    
    const response = await fetch(`${API_BASE_URL}/agents`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(agent),
    });
    
    if (response.status === 403) {
      const errorData = await response.json().catch(() => ({ error: 'Access denied' }));
      console.error('Permission error creating agent:', errorData);
      throw new Error(errorData.error || 'You do not have permission to use this workflow');
    }
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: `Server error (${response.status})` }));
      console.error('Error creating agent:', errorData);
      throw new Error(errorData.error || 'Failed to create agent');
    }
    
    console.log('Agent created successfully');
    return response.json();
  },

  async updateAgent(id: string, agent: Omit<Agent, 'id' | 'createdAt' | 'updatedAt'>): Promise<Agent> {
    const response = await fetch(`${API_BASE_URL}/agents/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(agent),
    });
    if (!response.ok) {
      throw new Error('Failed to update agent');
    }
    return response.json();
  },

  async deleteAgent(id: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/agents/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error('Failed to delete agent');
    }
  },

  async executeAgent(id: string, input: string): Promise<{ executionId: string }> {
    // Get agent API key from user or fallback to server
    const apiKey = await this.getAgentApiKey();
    
    const response = await fetch(`${API_BASE_URL}/public/agents/${id}/execute`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': apiKey
      },
      body: JSON.stringify({
        input
      }),
    });
    
    if (!response.ok) {
      // Handle specific error for invalid API key
      if (response.status === 401) {
        throw new Error('Invalid API key. Please check your agent API key in your profile.');
      }
      throw new Error('Failed to execute agent');
    }
    
    return response.json();
  },

  // Get server API key (this will be displayed to users for API calls)
  async getServerApiKey(): Promise<string> {
    try {
      // First try to get the API key from the health endpoint
      const healthResponse = await fetch(`${API_BASE_URL}/health`);
      if (healthResponse.ok) {
        const data = await healthResponse.json();
        if (data.api_key) {
          return data.api_key;
        }
      }
      
      // If that fails, try to get the API key from the API_KEYS.txt file
      const apiKeyFile = await fetch('/API_KEYS.txt');
      if (apiKeyFile.ok) {
        const content = await apiKeyFile.text();
        const lines = content.split('\n');
        for (const line of lines) {
          if (line.startsWith('API_KEY=')) {
            return line.substring(8).trim();
          }
        }
      }
    } catch (error) {
      console.error('Failed to get API key:', error);
    }
    
    // Use a dynamic placeholder instead of hardcoded key
    console.warn('No API key found from server or file, using placeholder');
    return `api-key-${Date.now()}`;
  },

  async getBlockUsage(blockId: string): Promise<{ used: boolean; workflows: Workflow[] }> {
    // First get all workflows
    const workflows = await this.getWorkflows();
    
    // Check if the block is used in any workflow
    const usedIn = workflows.filter(workflow => 
      workflow.blocks.some(block => block.blockId === blockId)
    );
    
    return {
      used: usedIn.length > 0,
      workflows: usedIn
    };
  },

  // Helper function to fix ownership of existing workflows
  async fixWorkflowsOwnership(): Promise<{ fixed: number, total: number }> {
    try {
      // Get the current user ID
      const userString = localStorage.getItem('user');
      if (!userString) {
        throw new Error('No user found in localStorage');
      }
      
      const user = JSON.parse(userString);
      if (!user?.id) {
        throw new Error('User ID not found in localStorage');
      }
      
      const userId = user.id;
      console.log(`Using user ID: ${userId} to fix workflows`);
      
      // Get all workflows
      const workflows = await this.getWorkflows();
      console.log(`Found ${workflows.length} workflows to check`);
      
      // Track the number of fixed workflows
      let fixedCount = 0;
      
      // Update each workflow without an owner
      for (const workflow of workflows) {
        // Check if the workflow already has a userId
        if (workflow.userId && workflow.userId === userId) {
          console.log(`Workflow '${workflow.name}' already owned by you`);
          continue;
        }
        
        // Set the userId
        workflow.userId = userId;
        
        // Update the workflow
        await this.updateWorkflow(workflow.id, workflow);
        fixedCount++;
        console.log(`Fixed ownership for workflow '${workflow.name}'`);
      }
      
      console.log(`Fixed ${fixedCount} of ${workflows.length} workflows`);
      return { fixed: fixedCount, total: workflows.length };
      
    } catch (error) {
      console.error('Error fixing workflows ownership:', error);
      throw error;
    }
  },

  // Metrics API methods
  async getAgentMetrics(agentId: string, monthYear?: string): Promise<AgentMonthlyMetrics> {
    const params = new URLSearchParams();
    if (monthYear) {
      params.append('month', monthYear);
    }
    
    const response = await fetch(`${API_BASE_URL}/metrics/agents/${agentId}?${params.toString()}`, {
      headers: getAuthHeaders(),
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch agent metrics');
    }
    
    return response.json();
  },

  async getUserMetrics(monthYear?: string): Promise<AgentMetricsSummary[]> {
    const params = new URLSearchParams();
    if (monthYear) {
      params.append('month', monthYear);
    }
    
    const response = await fetch(`${API_BASE_URL}/metrics/user?${params.toString()}`, {
      headers: getAuthHeaders(),
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch user metrics');
    }
    
    return response.json();
  },

  async getBillingMetrics(timeRange: MetricsTimeRange = 'month'): Promise<BillingMetrics> {
    const params = new URLSearchParams({ range: timeRange });
    
    const response = await fetch(`${API_BASE_URL}/metrics/billing?${params.toString()}`, {
      headers: getAuthHeaders(),
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch billing metrics');
    }
    
    return response.json();
  },

  // Prompt optimization methods
  async getPromptOptimizerSettings(): Promise<{model: string, prompt: string}> {
    const response = await fetch(`${API_BASE_URL}/admin/settings/prompt-optimizer`, {
      headers: getAuthHeaders(),
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch prompt optimizer settings');
    }
    
    return response.json();
  },

  async updatePromptOptimizerSettings(settings: {model?: string, prompt?: string}): Promise<{status: string, message: string}> {
    const response = await fetch(`${API_BASE_URL}/admin/settings/prompt-optimizer`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(settings),
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to update settings' }));
      throw new Error(error.error || 'Failed to update prompt optimizer settings');
    }
    
    return response.json();
  },

  async optimizePrompt(prompt: string): Promise<{optimizedPrompt: string, model: string}> {
    const response = await fetch(`${API_BASE_URL}/prompts/optimize`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ prompt }),
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to optimize prompt' }));
      throw new Error(error.error || 'Failed to optimize prompt');
    }
    
    return response.json();
  },
}; 